import AuxTabs from "@/components/AuxTabs";
import PageLoading from "@/components/PageLoading";
import Recipe from "@/components/Recipe";
import SimilarEntries from "@/components/SimilarEntries";
import UsefulLinksListing from "@/components/UsefulLinksListing";
import YouMightLikeEntries from "@/components/YouMightLikeEntries";
import type { AdConfigOptions } from "@/contexts/AdContext";
import { useAdContext } from "@/contexts/AdContext";
import { useHeaderContext } from "@/contexts/HeaderContext";
import { RecipeContextProvider } from "@/contexts/RecipeContext";
import { SessionContext } from "@/contexts/SessionContext";
import { recipeMetaLoaded, viewLockedRecipe } from "@/gtmEvents/recipe";
import useCurrentUser from "@/hooks/useCurrentUser";
import useFaqs from "@/hooks/useFaqs";
// import useSendConversionEventToAlgolia from "@/hooks/useSendConversionEventToAlgolia";
import useSubscriptionPermission from "@/hooks/useSubscriptionPermission";
import apiClient from "@/lib/apiClient";
import craft from "@/lib/craft";
import getCommonStaticProps from "@/lib/getCommonStaticProps";
import { pushEvent } from "@/lib/gtm";
import pushToDataLayer from "@/lib/pushToDataLayer";
import QueryRecipe from "@/queries/QueryRecipe.graphql";
import QueryRecipeSlugs from "@/queries/QueryRecipeSlugs.graphql";
import QueryRecipeQuantities from "@/queries/QueryRecipeQuantities.graphql";
import Ad from "@/src/ads/components/Ad";
import AdRosTop from "@/src/ads/components/AdRosTop";
import { useHashtagLabsContext } from "@/src/contexts/HashtagLabsContext";
import Notes from "@/src/notes/components/Notes";
import Reaction from "@/src/reactions/components/Reaction";
import RecipeHero from "@/src/recipes/components/RecipeHero";
import createRecipeFaqsJsonLd from "@/src/recipes/lib/createRecipeFaqsJsonLd";
import createRecipeJsonLd from "@/src/recipes/lib/createRecipeJsonLd";
import TestimonialsSection from "@components/TestimonialsSection";
import { Tab } from "@headlessui/react";
import clsx from "clsx";
import { observer } from "mobx-react";
import Head from "next/head";
import { useRouter } from "next/router";
import { useContext, useEffect, useState } from "react";
import { Seomatic } from "react-seomatic";
import { usePathname, useSearchParams } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import { useMediaQuery } from "@/src/hooks/useMediaQuery";
import { getEventDataFromRecipe } from "@/src/recipes/lib/getEventDataFromRecipe";
import createBreadcrumbsJsonLd from "@/src/recipes/lib/createBreadcrumbsJsonLd";
import ShoppingListContainer from "@/components/ShoppingList/ShoppingListContainer";
import useGTM from "@/hooks/useGTM";

export async function getStaticPaths() {
  // If production, set limit to 40, otherwise 1, so builds are faster
  const limit = process.env.VERCEL_ENV !== "production" ? 5 : 1;

  const response = await craft([
    {
      document: QueryRecipeSlugs,
      variables: { section: "recipes", limit },
    },
  ]);

  const { recipes } = response[0] || {};

  const paths = recipes.map((recipe) => ({
    params: { slug: recipe.slug },
  }));

  return { paths, fallback: "blocking" };
}

export async function getStaticProps({ params, previewData, preview }) {
  // Fetch common static properties
  const commonStaticProps = await getCommonStaticProps();

  const response = await craft(
    [
      {
        document: QueryRecipe,
        variables: {
          slug: params.slug,
          status: ["live", "pending", "disabled"],
          isMealPlan: false,
        },
      },
    ],
    preview ? previewData.token : null
  );

  const { recipe } = response[0];

  if (!recipe) {
    return {
      props: {
        ...commonStaticProps,
      },
      notFound: true,
    };
  }

  // If recipe.redirecTo is set, redirect to that URL
  if (recipe.redirectTo) {
    return {
      redirect: {
        destination: recipe.redirectTo,
        permanent: true,
      },
    };
  }

  let notes = [];
  let totalNotesIncludingReplies = 0;

  try {
    const notesResponse = await apiClient.get(
      `/notes/${recipe.id}`,
      undefined,
      {},
      false
    );

    if (notesResponse && notesResponse.success) {
      notes = notesResponse.notes;
      totalNotesIncludingReplies =
        notesResponse.metadata?.totalIncludingReplies;
    }
  } catch (error) {
    console.error("Error fetching notes:", error);
  }

  let rating = {
    ratingCount: 0,
    ratingValue: 0,
    ratingPriorCount: 0,
  };

  try {
    const ratingResponse = await apiClient.get(
      `/ratings/${recipe.id}`,
      undefined,
      {},
      false
    );

    if (ratingResponse?.success) {
      const { totalReactions, finalRating, priorCount } = ratingResponse.rating;

      rating = {
        ratingCount: totalReactions,
        ratingValue: finalRating,
        ratingPriorCount: priorCount,
      };
    }
  } catch (error) {
    console.error("Error fetching rating:", error);
  }

  const { seomatic, ...recipeDetails } = recipe;

  // Use the new augmentRecipeJsonLd function
  const recipeJsonLdSchema = createRecipeJsonLd({
    recipe,
    rating,
    notes,
  });
  const breadcrumbs = [
    { label: "Recipes", href: "/recipes" },
    { label: recipe.title, href: null },
  ];

  const jsonLdSchema = [
    recipeJsonLdSchema,
    createBreadcrumbsJsonLd(breadcrumbs),
  ];

  // Generate FAQs JSON-LD
  if (!!recipe.recipeFaqs && recipe.recipeFaqs.length > 0) {
    jsonLdSchema.push(createRecipeFaqsJsonLd(recipe.recipeFaqs));
  }

  return {
    props: {
      ...commonStaticProps,
      recipe: recipeDetails,
      seomatic,
      preview: preview ?? false,
      notes,
      totalNotesIncludingReplies,
      rating,
      jsonLdSchema,
      breadcrumbs,
    },
    revalidate: process.env.VERCEL_ENV !== "production" ? 1 : 3600,
  };
}

const RecipePage = ({
  recipe: initialRecipe,
  preview = false,
  globalFaqs,
  testimonials = [],
  notes,
  totalNotesIncludingReplies,
  rating,
}) => {
  const { sessionId, userId, guestId, utmString } = useContext(SessionContext);
  const { currentUser } = useCurrentUser();
  const [recipe, setRecipe] = useState(initialRecipe);

  const pathname = usePathname();
  const searchParams = useSearchParams();
  const posthog = usePostHog();

  const isSmallScreen = useMediaQuery("(max-width: 1024px)");

  // Configure the ads for this recipe
  const adConfig = {
    targeting: {
      articleID: recipe.id,
      env: process.env.NEXT_PUBLIC_VERCEL_ENV || "development",
      section: "recipes",
      Category: recipe.brands?.map((brand) => brand.title),
      url: recipe.url,
    },
  } as AdConfigOptions;

  const testimonialsToDisplay = testimonials?.filter((testimonial) =>
    testimonial.showTestimonialOnPages.includes("recipes")
  );

  // Show the header border
  const { setShowBorder } = useHeaderContext();

  useEffect(() => {
    setShowBorder(true);

    return () => {
      setShowBorder(false);
    };
  }, []);

  // Configure and enable/disable ads for this recipe
  const { setShowAds } = useAdContext(adConfig);

  useEffect(() => setShowAds(!recipe.hideAds), []);

  const { setPageTargeting } = useHashtagLabsContext();

  useEffect(() => {
    setPageTargeting({
      recipeId: recipe.id,
      section: "recipes",
      brands: recipe.brands?.map((brand) => brand.title),
      chefs: recipe.chefs?.map((chef) => chef.title),
      cuisines: recipe.cuisines?.map((cuisine) => cuisine.title),
      meals: recipe.meals?.map((meal) => meal.title),
      types: recipe.types?.map((type) => type.title),
      occasions: recipe.occasions?.map((occasion) => occasion.title),
      dietaryRequirements: recipe.dietaryRequirements?.map(
        (requirement) => requirement.title
      ),
    });
  }, [initialRecipe.id]);

  const forceRestriction =
    process.env.NEXT_PUBLIC_PAYWALL_ALL_RECIPES === "true" || false;

  const { subscriptionPermission, contentIsRestricted } =
    useSubscriptionPermission(initialRecipe, forceRestriction);

  useEffect(() => {
    if (preview) return;

    if (contentIsRestricted) {
      // TODO: remove legacy event
      pushEvent("mob_plus", {
        title: recipe.title,
        content_type: "recipes",
      });

      // Use the new approach to sending events
      pushToDataLayer(viewLockedRecipe, {
        id: recipe.id,
        title: recipe.title,
      });
    }
  }, [preview, contentIsRestricted, recipe]);

  const router = useRouter();

  /*
		GTM
	*/
  const gtm = useGTM();

  useEffect(() => {
    if (!recipe || !sessionId || !document || !guestId) return;

    if (!pathname || !posthog) return;

    let url = window.origin + pathname;
    if (searchParams.toString()) {
      url = url + `?${searchParams.toString()}`;
    }

    posthog.capture("$pageview", {
      $current_url: url,
    });

    const eventData = getEventDataFromRecipe(recipe);
    gtm.pushEvent("recipe_viewed", eventData);
  }, [
    recipe.id,
    sessionId,
    router,
    currentUser,
    utmString,
    pathname,
    searchParams,
    posthog,
  ]);

  // Push recipe details to the datalayer
  useEffect(() => {
    if (!recipe) return;

    const eventData = getEventDataFromRecipe(recipe);

    // TODO: remove legacy event
    pushEvent("recipeMeta", eventData);

    // Push recipe details to the datalayer
    pushToDataLayer(recipeMetaLoaded, eventData);
  }, [recipe.id]);

  const [tab, setTab] = useState(0);
  const [fetchingRecipeQuantities, setFetchingRecipeQuantities] =
    useState(false);

  const isRedacted =
    !subscriptionPermission.canViewMobPlusRecipes &&
    contentIsRestricted &&
    !preview;

  useEffect(() => {
    if (!isRedacted && !fetchingRecipeQuantities) {
      setFetchingRecipeQuantities(true);

      (async () => {
        const response = await craft([
          {
            document: QueryRecipeQuantities,
            variables: {
              id: initialRecipe.id,
              status: ["live", "pending", "disabled"],
            },
          },
        ]);

        const recipeIngredients = response[0].recipe.recipeIngredients;

        setRecipe(() => {
          const updatedRecipe = initialRecipe;
          updatedRecipe.recipeIngredients = updatedRecipe.recipeIngredients.map(
            (ingredient) => {
              const matchedIngredientWithQuantities = recipeIngredients.find(
                (item) => item.id === ingredient.id
              );

              if (matchedIngredientWithQuantities?.quantity) {
                ingredient.quantity = matchedIngredientWithQuantities.quantity;
              }

              if (matchedIngredientWithQuantities?.unit) {
                ingredient.unit = matchedIngredientWithQuantities.unit;
              }

              if (matchedIngredientWithQuantities?.label) {
                ingredient.label = matchedIngredientWithQuantities.label;
              }

              return ingredient;
            }
          );

          return updatedRecipe;
        });

        setFetchingRecipeQuantities(false);
      })();
    } else if (isRedacted) {
      setRecipe(initialRecipe);
    }
  }, [isRedacted, initialRecipe]);

  /*
		Algolia
	*/

  // useSendConversionEventToAlgolia(recipe); // TODO: no longer sending events to algolia; remove entirely eventually

  // If the page is not yet generated show a loading
  if (router.isFallback) {
    return <PageLoading />;
  }

  const isPremiumElement = parseInt(recipe.accessLevel) === 2;

  // Notes are enabled for all recipes
  const isNotesEnabled =
    process.env.NEXT_PUBLIC_ENABLE_NOTES_ON_RECIPES === "yes";

  return (
    <>
      <Seomatic Head={Head} {...recipe.seomatic} />
      <ShoppingListContainer hideOnScroll />
      <RecipeHero
        entry={recipe}
        ratingValue={rating?.ratingValue}
        totalNotesIncludingReplies={totalNotesIncludingReplies}
      />

      <AdRosTop />

      <RecipeContextProvider>
        <Recipe
          recipeEventData={getEventDataFromRecipe(recipe)}
          recipe={recipe}
          globalFaqs={globalFaqs}
          redacted={isRedacted}
          isLoading={fetchingRecipeQuantities}
        />
      </RecipeContextProvider>

      <div id="reaction" className={clsx("border-b border-t py-6 lg:py-12")}>
        <div className={clsx("mx-auto max-w-screen-sm px-6 lg:px-12")}>
          <Reaction
            elementId={recipe.id}
            isNotesEnabled={isNotesEnabled}
            isPremiumElement={isPremiumElement}
          />
        </div>
      </div>

      {isNotesEnabled && (
        <>
          <div id="notes" className={clsx("border-b py-6 lg:py-12")}>
            <div className={clsx("mx-auto max-w-screen-sm px-6 lg:px-12")}>
              <Notes
                initialNotes={notes}
                elementId={parseInt(recipe.id)}
                chefId={
                  recipe.chefs?.[0]?.id
                    ? parseInt(recipe.chefs[0].id)
                    : undefined
                }
                isPremiumElement={isPremiumElement}
              />
            </div>
          </div>
          <Ad
            placementName={
              process.env.NEXT_PUBLIC_ADS_PROVIDER === "freestar"
                ? "mob.co.uk_leaderboard_3"
                : "htlad-ros_postarticle"
            }
            className="py-6 lg:py-12"
          />
        </>
      )}

      {isSmallScreen && (
        <div className={clsx("block lg:hidden")}>
          <_AuxTabs recipe={recipe} globalFaqs={globalFaqs} />
        </div>
      )}

      {recipe.usefulLinks && recipe.usefulLinks.length > 0 ? (
        <Tab.Group
          onChange={(tab) => {
            setTab(tab);
          }}
          selectedIndex={tab}
        >
          <div className={clsx("px-6 pt-6 lg:px-12 lg:pt-12")}>
            <Tab.List
              className={clsx("tab-list-sm flex md:inline-flex print:!hidden")}
            >
              <Tab
                key="related-recipes"
                className={clsx("tab flex-1 md:flex-none")}
              >
                Related Recipes
              </Tab>
              <Tab
                key="useful-links"
                className={clsx("tab flex-1 md:flex-none")}
              >
                Useful Links
              </Tab>
            </Tab.List>
          </div>

          <Tab.Panels>
            <Tab.Panel key="related-recipes">
              <div
                className={clsx("space-y-6 py-6 pb-12 lg:space-y-12 lg:py-12")}
              >
                <SimilarEntries entryId={recipe.id} />
                <YouMightLikeEntries
                  entries={recipe.youMightLikeCarouselItems}
                  heading={recipe.youMightLikeHeading ?? undefined}
                />
              </div>
            </Tab.Panel>
            <Tab.Panel key="useful-links">
              <div className={clsx("p-6 lg:p-12")}>
                <UsefulLinksListing groups={recipe.usefulLinks} />
              </div>
            </Tab.Panel>
          </Tab.Panels>
        </Tab.Group>
      ) : (
        <>
          {recipe.status === "live" && (
            <div
              className={clsx(
                "space-y-6 py-6 pb-12 lg:space-y-12 lg:py-8 xl:py-12"
              )}
            >
              <SimilarEntries entryId={recipe.id} />
              <YouMightLikeEntries
                entries={recipe.youMightLikeCarouselItems}
                heading={recipe.youMightLikeHeading ?? undefined}
              />
            </div>
          )}
        </>
      )}

      <TestimonialsSection
        testimonials={testimonialsToDisplay}
        className="border-t py-6 lg:py-12"
      />
    </>
  );
};

const _AuxTabs = ({ recipe, globalFaqs }) => {
  const faqsToDisplay = useFaqs(
    globalFaqs.items,
    recipe.recipeFaqs,
    "recipes",
    false
  );

  return (
    <AuxTabs
      recipe={recipe}
      faqs={faqsToDisplay}
      faqIntro={globalFaqs.intro?.faqIntro}
    />
  );
};

export default observer(RecipePage);
